import moment from "moment-timezone";
import { weekDayToFullWeekName } from "../pages/personal/JournalPage";
import { weekDayToNumber } from "../constants/weekDayToNumber";
import { formatScheduleSlot } from "./scheduleSlotUtils";

const ISO_TO_SHORT = Object.fromEntries(
    Object.entries(weekDayToNumber).map(([short, iso]) => [iso, short]),
);

const WEEKDAY_NAME_TO_ISO = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
};

export function normalizeWeekdayIso(weekday) {
    if (typeof weekday === "number") {
        return weekday;
    }
    if (typeof weekday === "string") {
        const lowered = weekday.toLowerCase();
        if (WEEKDAY_NAME_TO_ISO[lowered]) {
            return WEEKDAY_NAME_TO_ISO[lowered];
        }
        const parsed = Number(weekday);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
}

export function scheduleItemsToFormState(scheduleItems = []) {
    const selectedWeekdays = [];
    const selectedTimes = {};

    scheduleItems.forEach((item) => {
        const weekdayIso = normalizeWeekdayIso(item.weekday);
        const short = ISO_TO_SHORT[weekdayIso];
        if (!short) return;

        selectedWeekdays.push(short);
        const start = (item.start_time || "").slice(0, 5);
        const end = (item.end_time || "").slice(0, 5);
        selectedTimes[short] = formatScheduleSlot(start, end, item.quantity || 1);
    });

    selectedWeekdays.sort(
        (a, b) => weekDayToNumber[a] - weekDayToNumber[b],
    );

    return { selectedWeekdays, selectedTimes };
}

export function scheduleItemsToLessonsSchedule(scheduleItems = [], timeZone, t = (value) => value) {
    return scheduleItems
        .map((item) => {
            const weekdayIso = normalizeWeekdayIso(item.weekday);
            const weekdayInfo = weekDayToFullWeekName(weekdayIso);
            const start = (item.start_time || "").slice(0, 5);
            const end = (item.end_time || "").slice(0, 5);
            const quantity = item.quantity || 1;

            return {
                weekDay: t(weekdayInfo.full),
                weekDayShort: weekdayInfo.short,
                time: `${start} - ${end} - ${t("{{count}} занятий", { count: quantity })}`,
            };
        })
        .sort((a, b) => weekDayToNumber[a.weekDayShort] - weekDayToNumber[b.weekDayShort]);
}

export function isPeriodActive(period, timeZone, referenceDate = moment()) {
    if (!period) return false;

    const today = referenceDate.clone().tz(timeZone).startOf("day");
    const start = moment(period.start_date).tz(timeZone).startOf("day");
    const end = period.end_date
        ? moment(period.end_date).tz(timeZone).endOf("day")
        : null;

    if (today.isBefore(start, "day")) return false;
    if (end && today.isAfter(end, "day")) return false;
    return true;
}

export function findActiveLearningPeriod(periods = [], timeZone, referenceDate = moment()) {
    if (!periods.length) return null;

    const active = periods.find((period) =>
        isPeriodActive(period, timeZone, referenceDate),
    );

    if (active) return active;

    const today = referenceDate.clone().tz(timeZone).startOf("day");
    const upcoming = [...periods]
        .filter((period) => moment(period.start_date).tz(timeZone).isSameOrAfter(today, "day"))
        .sort((a, b) => moment(a.start_date).valueOf() - moment(b.start_date).valueOf());

    return upcoming[0] || periods[0];
}

export function buildLearningPeriodsDisplay(periods = [], timeZone, referenceDate = moment(), t = (value) => value) {
    const active = findActiveLearningPeriod(periods, timeZone, referenceDate);
    const today = referenceDate.clone().tz(timeZone).startOf("day");

    return [...periods]
        .sort((a, b) => moment(a.start_date).valueOf() - moment(b.start_date).valueOf())
        .map((period) => {
            const isActive = active?.id === period.id && isPeriodActive(period, timeZone, referenceDate);
            const isUpcoming = moment(period.start_date).tz(timeZone).isAfter(today, "day");

            return {
                id: period.id,
                label: formatPeriodRangeLabel(period, timeZone, t),
                isActive,
                isUpcoming,
                statusLabel: t(isActive ? "текущий" : isUpcoming ? "будущий" : "завершён"),
                lessonsSchedule: scheduleItemsToLessonsSchedule(period.schedule_items || [], timeZone, t),
            };
        });
}

export function formatPeriodRangeLabel(period, timeZone, t = (value) => value) {
    if (!period) return null;
    const start = moment(period.start_date).tz(timeZone).format("DD.MM.YYYY");
    const end = period.end_date
        ? moment(period.end_date).tz(timeZone).format("DD.MM.YYYY")
        : t("без окончания");
    return `${start} – ${end}`;
}
